import { Injectable, Injector } from '@angular/core';

import * as feathersRx from 'feathers-reactive';
import * as io from 'socket.io-client';
import * as axios from 'axios';
import * as feathers from '@feathersjs/feathers';

import feathersSocketIOClient from '@feathersjs/socketio-client';
import feathersRESTClient from '@feathersjs/rest-client';
import feathersAuthClient from '@feathersjs/authentication-client';
import { environment } from '../../../../environments/environment';

import {
  BehaviorSubject,
  catchError,
  combineLatest,
  defer,
  forkJoin,
  from,
  iif,
  map,
  Observable,
  ObservableInput,
  of,
  ReplaySubject,
  retry,
  share,
  Subscription,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { SnackBarService } from '../snack-bar.service';
import { GlobalLoadingBarService } from '../global-loading-bar.service';
import { ApiEndpoint, LocalStorageKey } from '../../enums';
import { ApiSearchResponse } from '../../models/api-search-response';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from '../../models/user';
import { AuthResult } from '../../models/auth-result';
import { LingGlobalSettingsItem } from '../../models/ling-global-settings-item';
import { LingSettings } from '../../models/ling-settings';
import { TranslateService } from '@ngx-translate/core';
import { navigateToLoginWithNext } from '../../utils';

@Injectable({
  providedIn: 'root',
})
export class FeathersService {
  feathers = feathers();
  feathersREST = feathers();

  private socket = io.connect(environment.url, { transports: ['websocket'] });
  private rest = feathersRESTClient(environment.url);

  me$ = new BehaviorSubject<User | undefined>(undefined);
  settingsArray$ = new BehaviorSubject<LingGlobalSettingsItem[] | undefined>(
    undefined
  );
  settingsObject$ = new BehaviorSubject<LingSettings | undefined>(undefined);
  isVisitor = false;
  initialized$ = new ReplaySubject<boolean>(1);
  baseListenersSubscription?: Subscription;
  loadedTranslation = new ReplaySubject<object>(1);
  totalInitializeCalls: string[] = [];
  completeInitializeCalls: BehaviorSubject<string[]> = new BehaviorSubject<
    string[]
  >([]);
  initializeLoadingProgress = this.completeInitializeCalls.pipe(
    map(
      (completeCalls) =>
        (100 / (this.totalInitializeCalls.length || 1)) * completeCalls.length
    )
  );
  lastPipedError?: any;

  constructor(
    private message: SnackBarService,
    private progressBar: GlobalLoadingBarService,
    private router: Router,
    private route: ActivatedRoute,
    private injector: Injector
  ) {
    this.feathers
      .configure(feathersSocketIOClient(this.socket))
      .configure(feathersAuthClient())
      .configure(
        feathersRx({
          idField: '_id',
        })
      );

    this.feathersREST
      .configure(this.rest.axios(axios))
      .configure(feathersAuthClient())
      .configure(
        feathersRx({
          idField: '_id',
        })
      );
    this.me$.subscribe((user) => {
      this.isVisitor = !user;
    });

    this.feathers.authentication.handleSocket(this.socket);
    this.initialized$.next(false);
    if (this.existingToken && this.existingRefreshToken) {
      this.isVisitor = false;
      this.pipedFrom(this.reAuthenticate).subscribe();
    } else {
      this.initBaseListeners();
    }
  }

  get existingToken(): string | null {
    return localStorage.getItem(
      this.feathers.authentication.options.storageKey
    );
  }

  get existingRefreshToken(): string | null {
    return localStorage.getItem(LocalStorageKey.RefreshToken);
  }

  get existingUserId(): string | null {
    return localStorage.getItem(LocalStorageKey.UserId);
  }

  onError = (err: any, errNotification = true) => {
    this.lastPipedError = err;
    console.error(err);
    if (errNotification && err?.message) {
      this.message.error(err.message);
    }
    return throwError(err);
  };

  refreshToken() {
    return defer(() =>
      from(this.feathers.authentication.reset()).pipe(
        switchMap(() =>
          from<Observable<{ accessToken: string; refreshToken: string }>>(
            this.feathers.service(ApiEndpoint.RefreshTokens).create({
              _id: localStorage.getItem(LocalStorageKey.UserId),
              refreshToken: localStorage.getItem(LocalStorageKey.RefreshToken),
            })
          ).pipe(
            switchMap((res) => {
              localStorage.setItem(
                LocalStorageKey.AccessToken,
                res.accessToken
              );
              localStorage.setItem(
                LocalStorageKey.RefreshToken,
                res.refreshToken
              );
              return from(this.feathers.reAuthenticate()).pipe(
                switchMap(() => from(this.feathersREST.authentication.reset())),
                switchMap(() => from(this.feathersREST.reAuthenticate())),
                tap(() => {
                  this.feathers.authentication.handleSocket(this.socket);
                })
              );
            })
          )
        )
      )
    );
  }

  reAuthenticate = defer(() =>
    iif(
      () => !!this.existingRefreshToken && !!this.existingUserId,
      this.refreshToken().pipe(
        catchError((refreshTokenError) => {
          const translateService = this.injector.get(TranslateService);
          const currentLocation = this.router.url;
          this.isVisitor = true;
          if (!currentLocation.startsWith('/preview')) {
            translateService
              .get('snackbar.warn.authTokenExpired')
              .subscribe((text) => {
                this.message.warn(text);
              });
            navigateToLoginWithNext(this.router, this.route);
          }
          this.initBaseListeners();
          return throwError(refreshTokenError);
        }),
        tap((authResult: AuthResult) => {
          this.initialized$.pipe(take(1)).subscribe((init) => {
            if (!init) {
              this.initBaseListeners(authResult.user?._id!);
            }
          });
        })
      ),
      throwError(() => this.lastPipedError)
    )
  ).pipe(share());

  initBaseListeners(userId?: string): void {
    this.completeInitializeCalls.next([]);
    this.initialized$.next(false);
    if (this.baseListenersSubscription) {
      this.baseListenersSubscription.unsubscribe();
    }

    let initializationCalls: {
      translation: Observable<object>;
      fonts: Observable<FontFaceSet>;
      self?: Observable<User | undefined>;
      settings?: Observable<LingGlobalSettingsItem[] | undefined>;
    } =
      // Mandatory calls for any visitor
      {
        translation: this.loadedTranslation,
        fonts: from(document.fonts.ready),
      };

    // Calls for authorized user
    if (userId) {
      initializationCalls.self = this.watchSelf(userId!).pipe(
        tap((res) => {
          this.me$.next(res);
        }),
        catchError((err) => {
          this.me$.next(undefined);
          return throwError(err);
        })
      );
      initializationCalls.settings = this.watchSettings().pipe(
        tap((res) => {
          this.settingsArray$.next(res);
          this.settingsObject$.next(this.getMappedSettingsObject(res));
        }),
        catchError((err) => {
          return throwError(err);
        })
      );
    }
    for (let initializeCallsKey in initializationCalls) {
      (initializationCalls as any)[initializeCallsKey] = (
        initializationCalls as any
      )[initializeCallsKey].pipe(
        tap(() => {
          if (
            !this.completeInitializeCalls.value.includes(initializeCallsKey)
          ) {
            const nextValue = [...this.completeInitializeCalls.value];
            nextValue.push(initializeCallsKey);
            this.completeInitializeCalls.next(nextValue);
          }
        })
      );
    }
    this.totalInitializeCalls = Object.keys(initializationCalls);

    this.baseListenersSubscription = combineLatest<any>(
      initializationCalls
    ).subscribe((res: any) => {
      this.initialized$.next(true);
      if (userId) {
        this.me$.next(res.self!);
        this.settingsArray$.next(res.settings!);
      }
    });
  }

  pipedFrom<T>(
    input: ObservableInput<T>,
    errNotification = true
  ): Observable<T> {
    return defer(() =>
      this.progressBar.handleLoadingProgress(
        from(input).pipe(
          retry({
            count: 1,
            delay: (err) => {
              this.lastPipedError = err;
              const reAuthTriggerCodes = [401, 419];
              return err?.code && reAuthTriggerCodes.includes(err.code)
                ? this.reAuthenticate.pipe(
                    tap({
                      error: () => {
                        errNotification = false;
                      },
                    })
                  )
                : throwError(err);
            },
          }),
          catchError((err) => this.onError(err, errNotification))
        )
      )
    );
  }

  get<T>(ep: ApiEndpoint, id?: string, errNotification = true): Observable<T> {
    return this.pipedFrom(this.feathers.service(ep).get(id), errNotification);
  }

  post<T>(
    ep: ApiEndpoint,
    request: any,
    errNotification = true,
    query?: any
  ): Observable<T> {
    let params: any;
    if (query) {
      params = {};
      params.query = query;
    }
    return this.pipedFrom<T>(
      defer<Observable<T>>(() =>
        this.feathers.service(ep).create(request, params)
      ),
      errNotification
    );
  }

  put<T>(
    ep: ApiEndpoint,
    id: string,
    request: any,
    errNotification = true
  ): Observable<any> {
    return this.pipedFrom(
      this.feathers.service(ep).update(id, request),
      errNotification
    );
  }

  patch<T>(
    ep: ApiEndpoint,
    id: string,
    request: any,
    errNotification = true
  ): Observable<any> {
    return this.pipedFrom(
      defer(() => this.feathers.service(ep).patch(id, request)),
      errNotification
    );
  }

  delete<T>(
    ep: ApiEndpoint,
    id: string,
    errNotification = true
  ): Observable<any> {
    return this.pipedFrom(
      defer(() => this.feathers.service(ep).remove(id)),
      errNotification
    );
  }

  watchItem<T>(
    ep: ApiEndpoint,
    id: string,
    errNotification = true
  ): Observable<T> {
    return this.pipedFrom<T>(
      defer(() => this.feathers.service(ep).watch().get(id)) as Observable<T>,
      errNotification
    );
  }

  watchSearch<T>(
    ep: ApiEndpoint,
    query?: any,
    errNotification = true,
    matcher?: (
      query: any
    ) => (item: any, index: number, array: any[]) => boolean
  ): Observable<ApiSearchResponse<T>> {
    if (!query) {
      query = {};
    }
    if (!query?.$limit && query.$limit !== 0) {
      query.$limit = 1000;
    }
    let reactiveOptions: any = {};
    if (matcher) {
      reactiveOptions.matcher = matcher;
    }
    return this.pipedFrom<ApiSearchResponse<T>>(
      this.feathers.service(ep).watch(reactiveOptions).find(query),
      errNotification
    );
  }

  watchSelf(id: string): Observable<User | undefined> {
    return this.pipedFrom<User>(
      this.feathers.service(ApiEndpoint.Users).watch().get(id)
    );
  }

  watchSettings(): Observable<LingGlobalSettingsItem[] | undefined> {
    return this.pipedFrom<LingGlobalSettingsItem[]>(
      this.feathers.service(ApiEndpoint.Settings).watch().find()
    );
  }

  getMappedSettingsObject(
    settingsArray?: LingGlobalSettingsItem[]
  ): LingSettings {
    const result: any = {};
    if (!settingsArray) {
      return result;
    }
    settingsArray.forEach((settingItem) => {
      result[settingItem.key!] = settingItem.property?.value;
    });
    return result;
  }

  patchSettings(
    settingsArray: LingGlobalSettingsItem[]
  ): Observable<LingGlobalSettingsItem[]> {
    if (!settingsArray.length) {
      return of([]);
    }
    return forkJoin(
      settingsArray.map((setting) =>
        this.patch(ApiEndpoint.Settings, setting._id!, {
          property: {
            value: setting.property?.value,
            type: setting.property?.type,
          },
        })
      )
    );
  }
}
